/**
 * Returns `value` only when it is a safe internal path — one that starts with a
 * single "/" not followed by another "/" or "\". Anything else (non-string,
 * relative, protocol-relative "//host" or "/\host", or absolute "https://host")
 * returns undefined. The backslash case matters because the URL parser
 * normalizes "\" to "/", so "/\evil.com" resolves off-origin like "//evil.com".
 * Guards against open redirects from a crafted `?redirect=` param after login.
 */
export const sanitizeRedirect = (value: unknown): string | undefined =>
  typeof value === "string" && /^\/(?![/\\])/.test(value) ? value : undefined;

/**
 * Builds the navigation used to send a user to `/login` while preserving where
 * they were headed (full path + query via `href`). When already on `/login`,
 * keep the existing (sanitized) redirect instead of overwriting it with
 * "/login". Used by the axios logout handler.
 */
export const computeLoginNavigation = (location: {
  pathname: string;
  href: string;
  search: unknown;
}) => {
  const existingRedirect = (location.search as { redirect?: unknown } | null)
    ?.redirect;
  const redirect =
    location.pathname === "/login"
      ? sanitizeRedirect(existingRedirect)
      : sanitizeRedirect(location.href);

  return {
    to: "/login" as const,
    search: redirect ? { redirect } : {},
  };
};
